<template>
  <div class="comp__chart-overlay" @click="emit('toggle-compare-chart')"></div>
  <div class="comp__chart">
    <div class="comp__chart-inner">
      <div class="comp__chart-header">
        <button ref="backButton" @click="emit('toggle-compare-chart')" class="comp__header-left">
          <svg class="comp__header-back">
            <use xlink:href="#icon-prev"></use>
          </svg>
          <span class="comp__header-back-label">Back</span>
        </button>
        <div class="comp__header-right">
          <h5 class="comp__header-title">
            Comparing Products ({{ products.length }} items) 
          </h5>
          <button @click="emit('toggle-compare-chart')" aria-label="Close compare chart">
            <svg class="comp__header-close">
              <use xlink:href="#close"></use>
            </svg> 
          </button>
        </div>
      </div>
      <CompareTableDesktop
        v-if="windowWidth > 1023"
        :products="products"
        :uniqueHeadings="uniqueHeadings"
        :comparisonTable="comparisonTable"
        @on-remove-item="(product) => emit('on-remove-item', product)"
      />
      <CompareTableMobile
        v-else
        :products="products"
        :uniqueHeadings="uniqueHeadings"
        :comparisonTable="comparisonTable"
        @on-remove-item="(product) => emit('on-remove-item', product)"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, toRefs, computed, onMounted } from 'vue';
  import CompareTableDesktop from './compare-table-desktop.vue';
  import CompareTableMobile from './compare-table-mobile.vue';
  import useWindowResize from '../../../utils/use-window-resize';

  const props = defineProps({
    products: {
      type: Object,
      required: true,
    }
  })

  const { products } = toRefs(props)
  const emit = defineEmits(['toggle-compare-chart','on-remove-item'])
  const { windowWidth } = useWindowResize();
  const backButton = ref(null)

  onMounted(() => {
    window.refreshYotpoData();
    backButton.value.focus();
  })

  const uniqueHeadings = computed(() => {
    const uniqueMap = new Map();

    products.value.forEach(product => {
      product.compare_data.forEach(item => {
        const key = item.key;
        const heading = item.heading;

        // If the combination of key and heading doesn't already exist, add it
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, heading);
        }
      });
    });

    // Convert the map back into an array of objects with heading and key
    return Array.from(uniqueMap, ([key, heading]) => ({ key, heading }));
  });

  const comparisonTable = computed(() => {
    return products.value.map(product => {
      // Process the product data to handle multiple values per heading and key
      const productData = product.compare_data.reduce((acc, item) => {
        if (!acc[item.key]) {
          acc[item.key] = [];
        }
        acc[item.key].push({
          heading: item.heading,
          value: item.value,
          icon: item.icon || null // Add icon if available
        });
        return acc;
      }, {});

      // Ensure all keys are accounted for
      return uniqueHeadings.value.reduce((acc, headingKeyObj) => {
        const key = headingKeyObj.key;
        
        acc[key] = productData[key]?.length 
          ? productData[key] 
          : [{ value: 'None', icon: null }]; // Default if key is missing

        // Add product id if the key is "Ratings"
        if (key.toLowerCase() === "ratings" && acc[key].length) {
          acc[key][0].productId = product.id;
        }

        // Add fabric details if the key is "Fabric"
        if (key.toLowerCase() === "fabric" && acc[key].length) {
          acc[key][0].fabricDetails = getProductDetails(product, "Fabric Details");
        }

        // Add Measurements details if the key is "Measurements"
        if (key.toLowerCase() === "measurements" && acc[key].length) {
          acc[key][0].measurementsDetails = getProductDetails(product, "Fit and Measurements");
        }

        return acc;
      }, {});
    });
  });

  const getProductDetails = (product,dataKey) => {
    var details;
    const sectionMap = {
      'details': 'Details'
    };

    const { product_details, ...productInfo } = product || {};

    // Loop through product_info_ objects
    Object.entries(productInfo).forEach(([key, value]) => {
      if (key.startsWith('product_info_') && sectionMap[value.section_id]) {
        const sectionDetails = Object.keys(value)
          .filter(k => k.startsWith('title_'))
          .map((k, i) => ({
            title: value[`title_${i}`] || '',
            content: value[`content_${i}`] || ''
          }))
          .filter(({ title, content }) => title || content);

        const productDetails = sectionDetails.find(detail => detail.title === dataKey);

        if (productDetails) {
          details = productDetails.content;
        }
      }
    });

    // If not found in productInfo, search in product_details features
    if (!details && product_details) {
      const featuresSection = product_details.find(section => section.key === 'features');
      
      if (featuresSection && featuresSection.description) {
        const htmlContent = featuresSection.description;
        const fallbackRegex = new RegExp(`<p><strong>${dataKey}</strong></p>\\s*(.*?)(?=<p><strong>|$)`, 'is');
        const fallbackMatch = htmlContent.match(fallbackRegex);
        
        if (fallbackMatch) {
          details = fallbackMatch[1].trim();
        }
      }
    }
        
    return details;
  }
</script>